import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { MetaApi } from '../api';

const LocationContext = createContext(null);
const KEY = 'gt_location';

// Single source of truth for the delivery location. Prices across the site are
// fetched with this pincode so state/district pricing applies everywhere.
export function LocationProvider({ children }) {
  const [location, setLocation] = useState(null); // { pincode, city, district, state }
  const [serviceable, setServiceable] = useState(true);
  const [reason, setReason] = useState('');
  const [ready, setReady] = useState(false);

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(KEY) || 'null');
      if (saved?.pincode) setLocation(saved);
    } catch (_) { /* ignore */ }
    setReady(true);
  }, []);

  const persist = (loc) => {
    setLocation(loc);
    localStorage.setItem(KEY, JSON.stringify(loc));
  };

  // Resolve a pincode to state/district/city and check we deliver there.
  const setPincode = useCallback(async (pincode) => {
    const pin = String(pincode || '').replace(/\D/g, '').slice(0, 6);
    if (pin.length !== 6) throw new Error('Please enter a valid 6-digit pincode');

    const info = await MetaApi.pincode(pin);
    const loc = {
      pincode: pin,
      city: info.city || '',
      district: info.district || '',
      state: info.state || '',
    };

    const svc = await MetaApi.serviceability({
      pincode: pin,
      state: loc.state,
      district: loc.district,
    }).catch(() => ({ serviceable: true }));

    setServiceable(svc.serviceable !== false);
    setReason(svc.reason || '');
    persist(loc);
    return { loc, serviceable: svc.serviceable !== false, reason: svc.reason || '' };
  }, []);

  // Browser geolocation → reverse-lookup is not available server-side, so we
  // simply ask the user for a pincode if we can't infer one.
  const clear = useCallback(() => {
    localStorage.removeItem(KEY);
    setLocation(null);
  }, []);

  const label = location
    ? [location.city, location.district, location.state].filter(Boolean).filter((v, i, a) => a.indexOf(v) === i).join(', ')
    : '';

  return (
    <LocationContext.Provider
      value={{ location, label, serviceable, reason, ready, setPincode, clear, hasLocation: Boolean(location?.pincode) }}
    >
      {children}
    </LocationContext.Provider>
  );
}

export const useLocation = () => useContext(LocationContext);
